import React, { useState } from "react";

// Components
import { Helmet } from "react-helmet";
import { useQuery, useMutation } from "react-query";
import { useHistory, useParams } from "react-router";
import { Link } from "react-router-dom";

import { Order, Facility } from "@secondcloset/fulfillment-utils";
import { Fulfillment, Common } from "@secondcloset/types";
import {
  OrdersTable,
  CheckboxDropdown,
  DropdownButton,
  SearchOrganizationInput,
  SearchInput,
  ErrorsFilter,
} from "@secondcloset/web-components";

import { FilterFilled, FilterOutlined, SyncOutlined } from "@ant-design/icons";
import { Alert, Button, DatePicker, Space, Tooltip, Modal, message, Empty, Typography } from "antd";
import { SorterResult } from "antd/lib/table/interface";
import { debounce, isEmpty, startCase } from "lodash-es";
import moment, { Moment } from "moment";

import * as S from "./styles";

import { fetchOrganizationIndex } from "../../../api/accounts/organization";
import { bulkUpdateOrders, fetchOrderIndex } from "../../../api/fulfillment/order";
import { fetchShops } from "../../../api/integration/shopify";

// styles

// Hooks

import { useOrganizationContext } from "../../../contextProviders/organization/OrganizationProvider";
import { useFacilityIndex } from "../../../hooks/api/common/useFacilityIndex";
import useURLState from "../../../hooks/application/useURLStateV2";
import useUser from "../../../hooks/reduxContainer/_common/useUser";
import useOrderCreateFlow from "../../../hooks/reduxContainer/fulfillment/orderCreateFlow";
import useShipmentCreateFlow from "../../../hooks/reduxContainer/fulfillment/shipmentCreateFlow";

// api

// Libs

import {
  getShipmentTagOptions,
  getOrderStatusOptions,
  getStockStatusOptions,
  PAGE_LINKS,
  useOrderIndexQueryOptions,
  getFacililtyOptions,
} from "./helpers";
import { fetchInventoryChannels } from "../../../api/fulfillment/product";
import { titleCase } from "../../../components/SelectSalesChannel";

// Context

// Interfaces

type FacilityCode = Common.FacilityCode;
type MarkAsTag = "unfulfilled_items" | "ready_to_fulfill";
const { RangePicker } = DatePicker;

export const FulfillmentOrderIndexPage: React.FC = () => {
  const history = useHistory();
  const { user } = useUser();
  const { organization_id: orgID } = useParams<{ organization_id?: string }>();
  const [urlState, setUrlState] = useURLState({
    search: "",
    page: 1,
    pageSize: 10,
    shipmentTags: [],
    orderStatus: [],
    stockStatus: [],
    dateFrom: moment().subtract(3, "M").startOf("day").toISOString(),
    dateTo: moment().endOf("day").toISOString(),
    organizations: [],
    errorFilter: [],
    salesChannel: [],
    fulfilledFrom: [],
  });
  const [searchedOrganization, setSearchedOrganization] = useState("");
  const [sort, setSort] = useState<{
    field?: any;
    direction?: string | null;
  }>({ field: "created_at", direction: "" });
  const [selectedOrders, setSelectedOrders] = useState<Fulfillment.Order[]>([]);
  const [isShowFilters, setIsShowFilters] = useState(true);
  const { dispatchSetAutoCreateDeliveryShipment } = useOrderCreateFlow();
  const { dispatchSetIsReturn, dispatchResetShipmentCreateFlow } = useShipmentCreateFlow();
  const organizationDetails = useOrganizationContext();
  const organizationId = organizationDetails.data?.id;
  const queryOptions = useOrderIndexQueryOptions({ urlState, sort, orgID, userID: user.id });
  const facilityIndex = useFacilityIndex();
  const [isBulkUpdatingOrders, setIsBulkUpdatingOrders] = useState(false);
  const [isOrgConnectedToShopify, setIsOrgConnectedToShopify] = useState(false);

  const orderIndex = useQuery(["orderIndex", queryOptions], () => fetchOrderIndex(queryOptions), {
    keepPreviousData: true,
  });

  useQuery(["shopifyShops", organizationId], () => fetchShops({ organization_id: organizationId }), {
    enabled: !!organizationId,
    onSettled: (shops) =>
      setIsOrgConnectedToShopify(!!shops?.some((shop) => shop.active && !shop.fulfillment_service_mode_enabled)),
  });

  const organizationIndex = useQuery(
    ["organizationIndex", searchedOrganization],
    () => fetchOrganizationIndex({ q: searchedOrganization }),
    { enabled: searchedOrganization.length >= 3 && !orgID }
  );

  const { data: channels = [] } = useQuery(
    ["channels", organizationId],
    () => fetchInventoryChannels(organizationId || ""),
    {
      enabled: !!organizationId,
      select({ data }) {
        let options = data.map((d) => ({ key: d.name, value: d.name, label: titleCase(d.name) }));
        if (options.length === 0) options = [{ key: "wholesale", value: "wholesale", label: "Wholesale" }];
        return [...options, { key: "-", value: "-", label: "Unassigned" }];
      },
    }
  );

  const { mutate: onBulkUpdateOrders, ...bulkUpdatedOrders } = useMutation(bulkUpdateOrders, {
    onSuccess: async (data, variables) => {
      setIsBulkUpdatingOrders(true);
      return await new Promise((resolve) => {
        // eslint-disable-next-line
        selectedOrders.map((o) => {
          if (variables?.shipment_tag) o.shipment_tags = [variables?.shipment_tag];
          if (variables?.fulfilled_from) o.fulfilled_from = variables?.fulfilled_from;
        });
        resolve(setSelectedOrders(selectedOrders));
      }).then(() => {
        setIsBulkUpdatingOrders(false);
        const updatedSegment = variables.shipment_tag
          ? "Shipment tag"
          : variables.fulfilled_from
          ? "Fulfilled from facility"
          : "";
        message.success(`${updatedSegment} updated successfully.`);
      });
    },
    onSettled: () => setIsBulkUpdatingOrders(false),
  });

  const updateShipmentTag = (tag: MarkAsTag) => {
    const changeableOrders = selectedOrders.filter((o) => !o.shipment_tags?.includes(tag) && !o.cancelled);
    const toBeUpdatedOrderIDs = changeableOrders.map((o) => o.id);
    onBulkUpdateOrders({
      order_ids: toBeUpdatedOrderIDs,
      shipment_tag: tag,
    });
  };

  const updateOrdersFulfilledFrom = (facility: FacilityCode) => {
    onBulkUpdateOrders({
      order_ids: selectedOrders.map((o) => o.id),
      fulfilled_from: facility,
    });
  };

  const handleMarkAsSelectionChange = (key: MarkAsTag | FacilityCode) => {
    const isMarkingShipmentTags = ["unfulfilled_items", "ready_to_fulfill"].includes(key);
    const status = key === "unfulfilled_items" ? "Unfulfilled" : startCase(key);
    const newFacility = key.toUpperCase();
    const length = selectedOrders.length;
    Modal.confirm({
      title: `Are you sure you want to change ${length} ${length > 1 ? "orders" : "order"} to ${
        isMarkingShipmentTags ? status : newFacility
      }?`,
      okText: "Yes",
      cancelText: "No",
      okButtonProps: { loading: bulkUpdatedOrders.isLoading },
      onOk: () => {
        if (isMarkingShipmentTags) updateShipmentTag(key as MarkAsTag);
        else updateOrdersFulfilledFrom(key as FacilityCode);
      },
    });
  };

  const redirectTo = (link: string) => {
    history.push(`/organizations/${orgID}${link}`);
  };

  const updateDates = (dates: [Moment, Moment] | null) => {
    const [from, to] = dates ?? [];
    setUrlState((state) => ({
      ...state,
      dateFrom: from?.startOf("day").toISOString() || null,
      dateTo: to?.endOf("day").toISOString() || null,
    }));
  };

  const showOrderRecreateDisclaimer = (link: string) =>
    Modal.confirm({
      title: "Disclaimer",
      content: (
        <Space direction="vertical">
          <p>Recreate an order that has been fulfilled in your e-commerce site in order to create a return shipment.</p>
          <p>
            Please make sure all information is exactly the same as your order on your e-commerce site to prevent any
            data discrepancies.
          </p>
        </Space>
      ),
      okText: "I understand. Let's continue",
      onOk: () => {
        dispatchSetAutoCreateDeliveryShipment();
        redirectTo(link);
      },
    });

  const onCreateOrderDropdownClick = (key: string) => {
    if (key === "return") showOrderRecreateDisclaimer(PAGE_LINKS.reCreateOrder);
    else if (key === "manual") redirectTo(PAGE_LINKS.createOrder);
    else if (key === "csv") redirectTo(PAGE_LINKS.uploadCsv);
  };

  const buildCreateOrderDropdown = () => {
    if (!orgID) return null;
    const options = [
      { key: "manual", label: "Single Order" },
      { key: "csv", label: "Upload via CSV" },
      { key: "return", label: "Return Order" },
    ];

    return (
      <DropdownButton
        buttonText="Create Order"
        options={options}
        isDisabled={!!selectedOrders.length}
        onOptionSelect={onCreateOrderDropdownClick}
      />
    );
  };

  const checkIsShipmentTagUpdateDisabled = () => {
    const hasMultipleTags = selectedOrders.some((o) => o?.shipment_tags?.length > 1);
    const hasNoneEditableTag = selectedOrders.some(
      (o) => !o?.shipment_tags?.includes("unfulfilled_items") && !o?.shipment_tags?.includes("ready_to_fulfill")
    );
    return hasNoneEditableTag || hasMultipleTags;
  };

  const renderMarkSelectedAsDropdown = () => {
    const isShipmentTagUpdateDisabled = checkIsShipmentTagUpdateDisabled();
    const isLoading = bulkUpdatedOrders.status === "loading";
    const hasNoSelectedOrder = selectedOrders.length <= 0;
    const hasCancelledOrder = selectedOrders.some((o) => !!o?.cancelled);
    const facilitiesByProvince = Facility.getFacilityByProvince(facilityIndex.data ?? []);
    const subMenuOptions = [
      {
        title: "Shipment Tag",
        disabled: isShipmentTagUpdateDisabled,
        message: "Only active orders with Unfulfilled or Ready To Fulfill shipment tags can be updated",
        options: [
          { key: "unfulfilled_items", label: "Unfulfilled" },
          { key: "ready_to_fulfill", label: "Ready to Fulfill" },
        ],
      },
      {
        title: "Fulfilled From",
        options: Object.entries(facilitiesByProvince).map(([province, facilities]) => {
          return {
            title: province,
            options: facilities.map((f) => {
              return {
                key: f.code,
                label: f.name,
              };
            }),
          };
        }),
      },
    ];

    return (
      <DropdownButton
        buttonText="Mark Selected As"
        subMenuOptions={subMenuOptions}
        isLoading={isLoading || isBulkUpdatingOrders}
        isDisabled={hasNoSelectedOrder || hasCancelledOrder}
        tooltipMessage={
          hasNoSelectedOrder
            ? "Please select at least one order."
            : hasCancelledOrder
            ? "Cancelled orders cannot be updated."
            : undefined
        }
        onOptionSelect={(key) => handleMarkAsSelectionChange(key as MarkAsTag)}
      />
    );
  };

  const startCreateReturnShipment = () => {
    const selectedOrder = selectedOrders[0];
    dispatchResetShipmentCreateFlow();
    dispatchSetIsReturn(true);
    history.push(`/fulfillment/orders/${selectedOrder?.id}/create-shipment?isReturn=true`);
  };

  const getReturnShipmentDisabledMessage = () => {
    if (selectedOrders.length > 1) return "Please select only one order to return at a time";
    else if (selectedOrders.length === 1 && isEmpty(Order.getToBeReturnedItems(selectedOrders[0])))
      return "Selected order does not contain any returnable items.";
    else return "";
  };

  const buildCreateReturnShipmentButton = () => {
    if (!orgID) return null;
    const isOnlyOneOrderSelected = selectedOrders.length === 1;
    const selectedOrder = selectedOrders[0];
    const isCreateReturnShipmentDisabled =
      !isOnlyOneOrderSelected || (isOnlyOneOrderSelected && isEmpty(Order.getToBeReturnedItems(selectedOrder)));

    return (
      <Tooltip title={getReturnShipmentDisabledMessage()}>
        <Button onClick={startCreateReturnShipment} type="default" disabled={isCreateReturnShipmentDisabled}>
          Create Return Shipment
        </Button>
      </Tooltip>
    );
  };

  const renderTopButtons = () => {
    return (
      <Space direction="horizontal">
        {isOrgConnectedToShopify && renderOrderSyncButton()}
        {renderMarkSelectedAsDropdown()}
        {buildCreateReturnShipmentButton()}
        {buildCreateOrderDropdown()}
      </Space>
    );
  };

  const renderOrderSyncButton = () => {
    return (
      <Space style={{ marginLeft: "auto" }}>
        <Tooltip title="We will need to re-create a fulfilled order to make a return shipment">
          <Button type="link" icon={<SyncOutlined />} onClick={() => redirectTo(PAGE_LINKS.syncShopifyOrder)}>
            Pull Order from Shopify
          </Button>
        </Tooltip>
      </Space>
    );
  };

  const renderFilterWithLabel = (
    label: string,
    options: { key: string; label: string; value: string }[],
    selectedOptionKeys: string[],
    setSelectedOptionKeys: (selectedKeys: string[]) => void
  ) => {
    return (
      <S.FlexContainer>
        <S.Label>{label}</S.Label>
        <CheckboxDropdown
          options={options}
          defaultText="All"
          selectedOptionKeys={selectedOptionKeys}
          setSelectedOptionKeys={setSelectedOptionKeys}
          loading={orderIndex.isLoading}
          menuStyle={{ maxHeight: "400px", overflow: "scroll" }}
        />
      </S.FlexContainer>
    );
  };

  const renderOrderFilters = () => {
    const shipmentTagsOptions = getShipmentTagOptions(orgID);
    const orderStatusOptions = getOrderStatusOptions();
    const stockStatusOptions = getStockStatusOptions();
    const facilityOptions = getFacililtyOptions(facilityIndex.data || []);

    return (
      <S.FiltersContainer>
        {isShowFilters && (
          <S.FlexContainer>
            {renderFilterWithLabel("Shipment Tags", shipmentTagsOptions, urlState.shipmentTags, (shipmentTags) => {
              setUrlState((state) => ({ ...state, shipmentTags, page: 1 }));
            })}
            {renderFilterWithLabel("Order Status", orderStatusOptions, urlState.orderStatus, (orderStatus) =>
              setUrlState((state) => ({ ...state, orderStatus, page: 1 }))
            )}
            {renderFilterWithLabel("Order Stock", stockStatusOptions, urlState.stockStatus, (stockStatus) =>
              setUrlState((state) => ({ ...state, stockStatus, page: 1 }))
            )}
            {renderFilterWithLabel("Fulfilled From", facilityOptions, urlState.fulfilledFrom, (fulfilledFrom) =>
              setUrlState((state) => ({ ...state, fulfilledFrom, page: 1 }))
            )}
            {organizationId &&
              renderFilterWithLabel("Sales Channel", channels, urlState.salesChannel, (salesChannel) =>
                setUrlState((state) => ({ ...state, salesChannel, page: 1 }))
              )}
            <S.FlexContainer>
              <S.Label>Error</S.Label>
              <ErrorsFilter
                value={urlState.errorFilter}
                onChange={(errorFilter) => setUrlState((state) => ({ ...state, errorFilter }))}
              />
            </S.FlexContainer>
          </S.FlexContainer>
        )}
      </S.FiltersContainer>
    );
  };

  const renderOrderHeader = () => {
    return (
      <S.OrderHeader>
        <S.FlexColumnContainer>
          <S.FlexRowContainer>
            <Typography.Title level={4}>Orders</Typography.Title>
            {renderTopButtons()}
          </S.FlexRowContainer>
          <S.SearchContainer>
            <SearchInput
              placeholder="Search for orders"
              defaultValue={urlState.search}
              onSearch={(search) => setUrlState((state) => ({ ...state, search, page: 1 }))}
              onClear={() => setUrlState((state) => ({ ...state, search: "" }))}
            />
            {orgID ? null : (
              <SearchOrganizationInput
                mode="tags"
                placeholder="Search for clients"
                onSearch={debounce(setSearchedOrganization, 800)}
                loading={organizationIndex.isLoading}
                organizations={organizationIndex.data ?? []}
                onChange={(values: Record<"label" | "value", string>[]) =>
                  setUrlState((state) => ({
                    ...state,
                    organizations: values?.map((v) => v.label),
                    page: 1,
                  }))
                }
              />
            )}
            <S.FlexContainer style={{ marginLeft: "auto" }}>
              <RangePicker
                onChange={(dates) => updateDates(dates as [Moment, Moment] | null)}
                defaultValue={
                  urlState.dateFrom && urlState.dateTo ? [moment(urlState.dateFrom), moment(urlState.dateTo)] : null
                }
                disabledDate={(current) => {
                  if (!!orgID) return false;
                  const isTooLate = current > moment(urlState.dateFrom).add(3, "M").endOf("day");
                  const isTooEarly = current < moment(urlState.dateTo).subtract(3, "M").startOf("day");
                  return isTooLate || isTooEarly;
                }}
              />
            </S.FlexContainer>
            <Button type="text" onClick={() => setIsShowFilters(!isShowFilters)}>
              {isShowFilters ? <FilterFilled /> : <FilterOutlined />}
            </Button>
          </S.SearchContainer>
        </S.FlexColumnContainer>
        {renderOrderFilters()}
      </S.OrderHeader>
    );
  };

  const getSortDirection = (direction?: "ascend" | "descend" | null) => {
    if (direction === "ascend") return "ASC";
    if (direction === "descend") return "DESC";
    return "";
  };

  const handleTableChange = (sorter: SorterResult<any>) => {
    const { field, order } = sorter;
    setSort({ field: field, direction: getSortDirection(order) });
  };

  const buildOrdersTable = () => {
    return (
      <>
        {renderOrderHeader()}
        <OrdersTable
          orders={orderIndex.data?.data ?? []}
          isLoading={orderIndex.isPreviousData || orderIndex.isLoading}
          isFulfillmentClientVisible={!orgID}
          selectedOrders={selectedOrders}
          setSelectedOrders={setSelectedOrders}
          renderOrderLinkComponent={(displayText, orderID) => (
            <Link to={`/fulfillment/orders/${orderID}`}>{displayText}</Link>
          )}
          renderOrganizationLinkComponent={(displayText, orgID) => (
            <Link to={`/organizations/${orgID}`}>{displayText}</Link>
          )}
          onSort={handleTableChange}
          pagination={{
            size: "default",
            pageSize: urlState.pageSize,
            total: orderIndex.data?.meta.total_entries ?? 0,
            current: orderIndex.data?.meta.current_page ?? 0,
            showLessItems: true,
            showTotal: (total, [min, max]) => `Showing ${min}-${max} of ${total}`,
            onChange: (page, pageSize = 0) => {
              setUrlState((state) => ({ ...state, pageSize, page }));
            },
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Space direction="vertical">
                    <Typography.Text type="secondary">
                      Can&apos;t find your order? It may have been created more than 3 months ago.
                    </Typography.Text>
                    <Typography.Text type="secondary">
                      Please use Looker to find orders older than 3 months.
                    </Typography.Text>
                  </Space>
                }
              />
            ),
          }}
        />
      </>
    );
  };

  return (
    <div className="page-container" id="order-index-page">
      <Helmet title="Sierra - Fulfillment Orders" />
      {buildOrdersTable()}
      {orderIndex.error && (
        <Alert message={orderIndex.error as string} type="error" style={{ marginBottom: "16px" }} banner />
      )}
    </div>
  );
};
